import { AxiosError } from 'axios'
import { computed, flow, makeObservable, observable } from 'mobx'
import moment from 'moment'

import { requestWrapper } from 'Shared/utils'

import { Profile } from 'Core/common/Profile/types'
import { AppDateFormats, LOCALES } from 'Core/constants'
import { STORE_UI } from 'Core/constants/stores'
import { CustomGenerator } from 'Core/interfaces'
import {
	entityToLabelValue,
	getDefaultStoreArrayValue,
	getDefaultStoreBooleanValue,
	getDefaultStoreValue
} from 'Core/utils'

import { ProfileRepo } from 'Portals/Customer/repositories'
import { RootStore } from 'Portals/Customer/stores'
import i18n from 'Portals/Customer/utils/i18nService'

import 'moment/locale/fr'
import 'moment/locale/es'

export class ProfileStore {
	@observable accessor profile = getDefaultStoreValue<Profile.Basic>()
	@observable accessor locales = getDefaultStoreArrayValue<Profile.Locale>()
	@observable accessor isEhsEnabled = getDefaultStoreBooleanValue()

	constructor(public rootStore: RootStore) {
	}

	@computed
	get titleName() {
		const { profile } = this
		if (!profile.value) {
			return ''
		}

		return `${profile.value.firstName} ${profile.value.lastName}`
	}

	@computed
	get localesOptions(): LabelValue<number>[] {
		return this.locales.value.map(entityToLabelValue)
	}

	@flow.bound
	*retrieveInitialData() {
		this.rootStore[STORE_UI].setIsDateFormatLoading(true)
		try {
			yield this.retrieveProfile()

			const selectedLocaleId = this.profile.value?.selectedLocale?.id

			if (selectedLocaleId) {
				yield this.retrieveLocales()
				const dateFormat = this.locales.value.find(
					(locale) => selectedLocaleId === locale.id
				)?.dateFormat as AppDateFormats
				if (dateFormat) {
					this.rootStore[STORE_UI].setCurrentDateFormat(dateFormat)
				}
			}
		} catch (error) {
			this.rootStore[STORE_UI].showErrorNotification(error as AxiosError)
		} finally {
			this.rootStore[STORE_UI].setIsDateFormatLoading(false)
		}
	}

	@flow.bound
	*retrieveProfile(): CustomGenerator<Profile.Basic | TFunction> {
		try {
			const profile = yield ProfileRepo.retrieveProfile()
			const correctProfile = profile as Profile.Basic
			this.profile.value = {
				...correctProfile,
				email: correctProfile.email,
				fax: correctProfile.fax,
				selectedLocale: correctProfile.selectedLocale,
				selectedLanguage: correctProfile.selectedLanguage
			}
			if (correctProfile.selectedLanguage?.name) {
				const lng = LOCALES[correctProfile.selectedLanguage.name]
				yield i18n.changeLanguage(lng)
				moment.locale(lng)
			}
		} catch (error) {
			this.rootStore[STORE_UI].showErrorNotification(error as AxiosError)
		}
	}

	@flow.bound
	*retrieveLocales(): CustomGenerator<Profile.Locale[]> {
		yield requestWrapper({
			field: this.locales,
			repository: ProfileRepo.retrieveLocales,
			repositoryParams: null
		})
	}

	@flow.bound
	*retrieveEhsEnabled(): CustomGenerator<Profile.Locale[]> {
		yield requestWrapper({
			field: this.isEhsEnabled,
			repository: ProfileRepo.retrieveEhsEnabled,
			repositoryParams: null
		})
	}

	@flow.bound
	*updateProfile(params: Profile.Request.Edit) {
		this.rootStore[STORE_UI].setIsRequestSubmitting()
		try {
			yield ProfileRepo.updateProfile(params)
			this.rootStore[STORE_UI].showSuccessNotification(
				i18n.t('constants:PROFILE_SAVE_MESSAGE')
			)
			yield this.retrieveProfile()

			const selectedLocaleId = this.profile.value?.selectedLocale?.id

			if (selectedLocaleId) {
				yield this.retrieveLocales()

				const selectedDateFormat = this.locales.value.find(
					(locale) => selectedLocaleId === locale.id
				)?.dateFormat

				if (selectedDateFormat) {
					this.rootStore[STORE_UI].setCurrentDateFormat(
						selectedDateFormat as AppDateFormats
					)
				}
			}
		} catch (error) {
			this.rootStore[STORE_UI].showErrorNotification(error as AxiosError)
		}
		this.rootStore[STORE_UI].setIsRequestSubmitting(false)
	}
}
